import { useCallback, useEffect, useRef, useState } from 'react';
import { useSyncExternalStore } from 'react';
import { storage } from '../websocket/storage';
import {
  ANALYSIS_TERMINAL_STATUSES,
  AnalysisProgressData,
  STORAGE_STATUS_TO_ENUM,
} from '../websocket/domains/analysis/analysis.types';
import { useStorage } from './useStorage';

export type { AnalysisProgressData };
export { ANALYSIS_TERMINAL_STATUSES, STORAGE_STATUS_TO_ENUM };

export type AnalysisProgressMap = Record<number, AnalysisProgressData | null>;

export interface AnalysisProgressMapResult {
  progressMap: AnalysisProgressMap;
  /** Incrementa a cada novo ID que atinge status terminal. Use em useEffect para disparar refresh. */
  terminalEpoch: number;
}

export function useAnalysisProgress(id: number): AnalysisProgressData | null {
  return useStorage<AnalysisProgressData>(`analysis_${id}`);
}

export function useAnalysisProgressMap(ids: number[]): AnalysisProgressMapResult {
  const snapshotRef = useRef<AnalysisProgressMap>({});
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const notifiedTerminalRef = useRef<Set<number>>(new Set());
  const [terminalEpoch, setTerminalEpoch] = useState(0);

  const subscribe = useCallback(
    (cb: () => void) =>
      storage.subscribe((key) => {
        if (key === null || key.startsWith('analysis_')) cb();
      }),
    [],
  );

  const getSnapshot = useCallback((): AnalysisProgressMap => {
    const next: AnalysisProgressMap = {};
    for (const id of idsRef.current) {
      next[id] = storage.get<AnalysisProgressData>(`analysis_${id}`);
    }
    if (JSON.stringify(next) !== JSON.stringify(snapshotRef.current)) {
      snapshotRef.current = next;
    }
    return snapshotRef.current;
  }, []);

  const progressMap = useSyncExternalStore(subscribe, getSnapshot, () => ({} as AnalysisProgressMap));

  useEffect(() => {
    let changed = false;
    for (const id of ids) {
      const data = progressMap[id];
      if (
        data &&
        ANALYSIS_TERMINAL_STATUSES.includes(data.status) &&
        !notifiedTerminalRef.current.has(id)
      ) {
        notifiedTerminalRef.current.add(id);
        changed = true;
      }
    }
    if (changed) setTerminalEpoch((e) => e + 1);
  }, [progressMap, ids]);

  return { progressMap, terminalEpoch };
}
